import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  useTheme,
  Chip,
  Stack,
  Tooltip,
  Fade,
  Button,
} from '@mui/material';
import {
  Public,
  Language,
  LocationOn,
  ZoomIn,
  ZoomOut,
  Room,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import SectionTitle from '../common/SectionTitle';

const CoverageMap = () => {
  const theme = useTheme();
  const [hoveredRegion, setHoveredRegion] = useState(null);
  const [zoom, setZoom] = useState(1);
  
  const regions = [
    { 
      name: 'North America', 
      coverage: '95%', 
      countries: 3, 
      cities: 245,
      pops: 85,
      color: '#0066CC',
      x: '15%',
      y: '30%',
      description: 'Extensive fiber network across USA, Canada, and Mexico'
    },
    { 
      name: 'Europe', 
      coverage: '92%', 
      countries: 45, 
      cities: 580,
      pops: 120,
      color: '#00A3FF',
      x: '48%',
      y: '28%',
      description: 'Connected to all major European hubs'
    },
    { 
      name: 'Asia Pacific', 
      coverage: '88%', 
      countries: 38, 
      cities: 420,
      pops: 95,
      color: '#00C853',
      x: '75%',
      y: '45%',
      description: 'Fastest growing network in Asia'
    },
    { 
      name: 'Middle East', 
      coverage: '85%', 
      countries: 12, 
      cities: 85,
      pops: 35,
      color: '#FF6B00',
      x: '55%',
      y: '42%',
      description: 'Strategic connectivity hub'
    },
    { 
      name: 'Africa', 
      coverage: '75%', 
      countries: 25, 
      cities: 120,
      pops: 28,
      color: '#FFC107',
      x: '52%',
      y: '55%',
      description: 'Rapidly expanding coverage'
    },
    { 
      name: 'Latin America', 
      coverage: '82%', 
      countries: 20, 
      cities: 180,
      pops: 42,
      color: '#9C27B0',
      x: '32%',
      y: '65%',
      description: 'Comprehensive coverage across the region'
    },
  ];

  const handleZoomIn = () => {
    if (zoom < 1.5) setZoom(prev => prev + 0.1);
  };

  const handleZoomOut = () => {
    if (zoom > 0.8) setZoom(prev => prev - 0.1);
  };

  return (
    <Box sx={{ py: 10, bgcolor: 'background.default', position: 'relative', overflow: 'hidden' }}>
      <Container maxWidth="xl">
        <SectionTitle
          title="Global Coverage"
          subtitle="Connected across 150+ countries with 500+ points of presence worldwide"
        />

        <Grid container spacing={4}>
          {/* Interactive Map */}
          <Grid item xs={12} md={7}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Paper
                sx={{
                  position: 'relative',
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: theme.shadows[4],
                  bgcolor: theme.palette.grey[50],
                }}
              >
                {/* Zoom Controls */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 20,
                    right: 20,
                    zIndex: 10,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleZoomIn}
                    sx={{ minWidth: 36, width: 36, height: 36, borderRadius: 2 }}
                  >
                    <ZoomIn />
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleZoomOut}
                    sx={{ minWidth: 36, width: 36, height: 36, borderRadius: 2 }}
                  >
                    <ZoomOut />
                  </Button>
                </Box>

                <Box
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: 'zoom-in',
                  }}
                >
                  <motion.div
                    animate={{ scale: zoom }}
                    transition={{ duration: 0.3 }}
                  >
                    <Box
                      component="img"
                      src="/assets/images/world-map-detailed.png"
                      alt="Global Coverage Map"
                      sx={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                      }}
                      onError={(e) => {
                        e.target.src = 'https://upload.wikimedia.org/wikipedia/commons/8/80/World_map_-_low_resolution.svg';
                      }}
                    />
                    
                    {/* Interactive Dots */}
                    {regions.map((region, idx) => (
                      <Tooltip
                        key={idx}
                        title={
                          <Box sx={{ p: 1 }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {region.name}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Coverage: {region.coverage}
                            </Typography>
                            <Typography variant="caption" display="block">
                              POPs: {region.pops}
                            </Typography>
                          </Box>
                        }
                        placement="top"
                        arrow
                      >
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: idx * 0.1, type: 'spring' }}
                          style={{
                            position: 'absolute',
                            left: region.x,
                            top: region.y,
                            transform: 'translate(-50%, -50%)',
                          }}
                        >
                          <Box
                            onMouseEnter={() => setHoveredRegion(idx)}
                            onMouseLeave={() => setHoveredRegion(null)}
                            sx={{
                              width: hoveredRegion === idx ? 20 : 12,
                              height: hoveredRegion === idx ? 20 : 12,
                              bgcolor: region.color,
                              borderRadius: '50%',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              boxShadow: `0 0 0 4px ${region.color}40`,
                              animation: 'pulse 2s infinite',
                              '@keyframes pulse': {
                                '0%': {
                                  boxShadow: `0 0 0 0 ${region.color}80`,
                                },
                                '70%': {
                                  boxShadow: `0 0 0 10px ${region.color}00`,
                                },
                                '100%': {
                                  boxShadow: `0 0 0 0 ${region.color}00`,
                                },
                              },
                            }}
                          />
                        </motion.div>
                      </Tooltip>
                    ))}
                  </motion.div>
                </Box>

                {/* Stats Overlay */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 20,
                    left: 20,
                    right: 20,
                    display: 'flex',
                    gap: 2,
                    flexWrap: 'wrap',
                    zIndex: 10,
                  }}
                >
                  <Chip
                    icon={<Language />}
                    label="150+ Countries"
                    sx={{ bgcolor: 'rgba(0,0,0,0.7)', color: 'white' }}
                  />
                  <Chip
                    icon={<LocationOn />}
                    label="500+ POPs"
                    sx={{ bgcolor: 'rgba(0,0,0,0.7)', color: 'white' }}
                  />
                  <Chip
                    icon={<Public />}
                    label="50+ Data Centers"
                    sx={{ bgcolor: 'rgba(0,0,0,0.7)', color: 'white' }}
                  />
                </Box>
              </Paper>
            </motion.div>
          </Grid>

          {/* Region Details */}
          <Grid item xs={12} md={5}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
                Regional Coverage
              </Typography>
              
              <Stack spacing={2}>
                {regions.map((region, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    viewport={{ once: true }}
                  >
                    <Paper
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        border: hoveredRegion === idx ? `2px solid ${region.color}` : 'none',
                        bgcolor: hoveredRegion === idx ? region.color + '10' : 'background.paper',
                        '&:hover': {
                          transform: 'translateX(8px)',
                          boxShadow: theme.shadows[4],
                        },
                      }}
                      onMouseEnter={() => setHoveredRegion(idx)}
                      onMouseLeave={() => setHoveredRegion(null)}
                    >
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4}>
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ color: region.color }}>
                            {region.name}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={8}>
                          <Box sx={{ mb: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                Coverage
                              </Typography>
                              <Typography variant="caption" fontWeight="bold">
                                {region.coverage}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                height: 6,
                                bgcolor: theme.palette.grey[200],
                                borderRadius: 3,
                                overflow: 'hidden',
                              }}
                            >
                              <motion.div
                                initial={{ width: 0 }}
                                whileInView={{ width: region.coverage }}
                                transition={{ duration: 1, delay: idx * 0.1 }}
                                style={{
                                  height: '100%',
                                  backgroundColor: region.color,
                                  borderRadius: 3,
                                }}
                              />
                            </Box>
                          </Box>
                          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              📍 {region.countries} Countries
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              🏙️ {region.cities} Cities
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              📡 {region.pops} POPs
                            </Typography>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Paper>
                  </motion.div>
                ))}
              </Stack>

              {/* Expansion Note */}
              <Box
                sx={{
                  mt: 4,
                  p: 3,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  color: 'white',
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Public sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      Expanding to 180+ Countries by 2025
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      We're constantly growing our network to serve you better
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default CoverageMap;